import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getClienteId, setClienteId } from "../lib/api.js";
import { setSentryUser, clearSentryUser } from "../lib/sentry.js";

function hasToken() {
  try { return !!localStorage.getItem("portal_session"); } catch { return false; }
}

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clienteActivoId, setActivo] = useState(getClienteId());
  const [loading, setLoading] = useState(true);

  // Cargar sesión inicial — SOLO si hay token, sino skip directo (evita loop)
  useEffect(() => {
    if (!hasToken()) {
      setLoading(false);
      return;
    }

    // Safety: si en 8s no resolvimos, forzamos loading=false para no quedar atrapados
    const safetyTimeout = setTimeout(() => {
      console.warn("[Portal/Auth] timeout — forzando salida del loading");
      setLoading(false);
    }, 8000);

    (async () => {
      try {
        // Wrap api.me() en un timeout más corto (5s) para detectar SW/red colgada
        const meWithTimeout = Promise.race([
          api.me(),
          new Promise((_, rej) => setTimeout(() => rej(new Error("me() timeout")), 5000)),
        ]);
        const r = await meWithTimeout;
        setUser(r.user);
        setSentryUser(r.user);
        setClientes(r.clientes || []);
        if (!clienteActivoId && r.clientes?.length === 1) {
          setActivo(r.clientes[0].id);
          setClienteId(r.clientes[0].id);
        }
      } catch (err) {
        console.warn("[Portal/Auth] me() falló:", err.message);
        setUser(null); setClientes([]);
        // Si fue timeout y hay token, probablemente SW corrupto — limpiar
        if (err.message === "me() timeout" && hasToken()) {
          setToken(null);
          setClienteId(null);
        }
      } finally {
        clearTimeout(safetyTimeout);
        setLoading(false);
      }
    })();
  }, []);

  const loginWithToken = useCallback(async (sessionToken, sessionUser, sessionClientes) => {
    setToken(sessionToken);
    setUser(sessionUser);
    setSentryUser(sessionUser);
    setClientes(sessionClientes || []);
    if (sessionClientes?.length === 1) {
      setActivo(sessionClientes[0].id);
      setClienteId(sessionClientes[0].id);
    }
  }, []);

  const elegirCliente = useCallback((id) => {
    setActivo(id);
    setClienteId(id);
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch {}
    setToken(null); setClienteId(null);
    setUser(null); setClientes([]); setActivo(null);
    clearSentryUser();
    // No usar window.location — solo limpiar state. El Protected redirige a /login.
  }, []);

  const clienteActivo = clientes.find(c => c.id === clienteActivoId);

  // v8.5.40 · F1 · Piel Estudio — el portal ya no se tiñe con el color de
  // marca del cliente; el acento es fijo (--accent, teal). El color de marca
  // sobrevive solo en el avatar/inicial del cliente (Sidebar.jsx, Layout.jsx).

  return (
    <Ctx.Provider value={{
      user, clientes, clienteActivo, clienteActivoId, loading,
      loginWithToken, elegirCliente, logout,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() { return useContext(Ctx); }
